/**
 * selectionHighlightBus
 *
 * Typed pub/sub bus for the universal "add text to chat" feature. When a
 * selection is added/removed from the chat context (via
 * `selectionToChatService` or `contextStore.addTextExcerpt`), this bus
 * broadcasts an event so the source app can render (or clear) the blue
 * in-place highlight.
 *
 * Subscription is keyed on `(kind, entityLlmId)` so each open document only
 * processes events relevant to itself.
 */

export type SelectionHighlightKind =
  | 'docs'
  | 'notes'
  | 'sheets'
  | 'canvas'
  | 'slides';

export type SelectionHighlightSublocation =
  /** ProseMirror text selection inside a doc-like app. */
  | {
      type: 'prosemirror-range';
      from: number;
      to: number;
      /** Optional extra: identifies a nested editor (e.g. slide element id). */
      containerId?: string;
    }
  /** Single cell inside a spreadsheet. */
  | {
      type: 'cell';
      row: number;
      col: number;
      a1?: string;
    }
  /** Range of cells inside a spreadsheet. */
  | {
      type: 'cell-range';
      startRow: number;
      startCol: number;
      endRow: number;
      endCol: number;
      a1?: string;
    }
  /** Substring selection inside a single spreadsheet cell's editor. */
  | {
      type: 'cell-text';
      row: number;
      col: number;
      from: number;
      to: number;
      a1?: string;
    }
  /** A single canvas/slide element (sticky note, text box, shape, …). */
  | {
      type: 'element';
      elementId: string;
    }
  /** Multiple canvas/slide elements selected together. */
  | {
      type: 'element-set';
      elementIds: string[];
    }
  /** Text selection inside a cell's or element's nested rich-text editor. */
  | {
      type: 'nested-prosemirror-range';
      containerId: string;
      from: number;
      to: number;
    };

export interface SelectionHighlightAddEvent {
  id: string;
  kind: SelectionHighlightKind;
  entityLlmId: string;
  text: string;
  sublocation?: SelectionHighlightSublocation;
}

export interface SelectionHighlightRemoveEvent {
  id: string;
}

interface Handlers {
  onAdd?: (event: {
    id: string;
    text: string;
    sublocation?: SelectionHighlightSublocation;
  }) => void;
  onRemove?: (event: { id: string }) => void;
  onClearAll?: () => void;
}

class SelectionHighlightBus {
  /** Map of `${kind}::${entityLlmId}` -> set of handler objects. */
  private subscribers = new Map<string, Set<Handlers>>();

  private key(kind: SelectionHighlightKind, entityLlmId: string): string {
    return `${kind}::${entityLlmId}`;
  }

  subscribe(
    kind: SelectionHighlightKind,
    entityLlmId: string,
    handlers: Handlers
  ): () => void {
    const key = this.key(kind, entityLlmId);
    let set = this.subscribers.get(key);
    if (!set) {
      set = new Set<Handlers>();
      this.subscribers.set(key, set);
    }
    set.add(handlers);
    return () => {
      const s = this.subscribers.get(key);
      if (!s) return;
      s.delete(handlers);
      if (s.size === 0) this.subscribers.delete(key);
    };
  }

  emitAdd(event: SelectionHighlightAddEvent): void {
    const key = this.key(event.kind, event.entityLlmId);
    const set = this.subscribers.get(key);
    if (set) {
      set.forEach((h) =>
        h.onAdd?.({
          id: event.id,
          text: event.text,
          sublocation: event.sublocation,
        })
      );
    }
  }

  /**
   * Remove events are broadcast to all subscribers — we don't know at emit
   * time which entity the id belonged to, and each subscriber filters by id
   * internally.
   */
  emitRemove(event: SelectionHighlightRemoveEvent): void {
    this.subscribers.forEach((set) => {
      set.forEach((h) => h.onRemove?.(event));
    });
  }

  emitClearAll(): void {
    this.subscribers.forEach((set) => {
      set.forEach((h) => h.onClearAll?.());
    });
  }

}

export const selectionHighlightBus = new SelectionHighlightBus();
